import React, { useEffect, useState } from 'react';
import { X, Folder, FileText, ArrowLeft, Link, Download, Loader } from 'lucide-react';
import { integrationsApi } from '../services/api';
import { useToast } from '../context/ToastContext';

interface DriveFile {
  id: string;
  name: string;
  mimeType: string;
  size?: string;
  modifiedTime?: string;
  webViewLink?: string;
  iconLink?: string;
}

interface CloudFilePickerProps {
  open: boolean;
  onClose: () => void;
  clientId?: string;
  onFileLinked?: (doc: { id: string; file_name: string; source: string; cloud_view_url?: string }) => void;
  onFileImported?: (doc: { id: string; file_name: string; source: string; file_size_bytes?: number }) => void;
}

export default function CloudFilePicker({ open, onClose, clientId, onFileLinked, onFileImported }: CloudFilePickerProps) {
  const { showToast } = useToast();
  const [files, setFiles] = useState<DriveFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [folderStack, setFolderStack] = useState<{ id: string | undefined; name: string }[]>([{ id: undefined, name: 'Mein Drive' }]);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const currentFolder = folderStack[folderStack.length - 1];

  useEffect(() => {
    if (open) {
      loadFiles(currentFolder.id);
    }
  }, [open, currentFolder.id]);

  const loadFiles = async (parentId?: string) => {
    setLoading(true);
    try {
      const res = await integrationsApi.googleDriveFiles(parentId);
      setFiles(res.data.files || []);
    } catch {
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  const navigateToFolder = (folder: DriveFile) => {
    setFolderStack([...folderStack, { id: folder.id, name: folder.name }]);
  };

  const navigateBack = () => {
    if (folderStack.length > 1) {
      setFolderStack(folderStack.slice(0, -1));
    }
  };

  const handleLink = async (file: DriveFile) => {
    setActionLoading(file.id);
    try {
      const res = await integrationsApi.googleDriveLink(file.id, clientId);
      onFileLinked?.(res.data);
      onClose();
    } catch {
      showToast('Verknüpfen fehlgeschlagen', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleImport = async (file: DriveFile) => {
    setActionLoading(file.id);
    try {
      const res = await integrationsApi.googleDriveImport(file.id, clientId);
      onFileImported?.(res.data);
      onClose();
    } catch {
      showToast('Import fehlgeschlagen', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const isFolder = (file: DriveFile) => file.mimeType === 'application/vnd.google-apps.folder';

  const formatSize = (size?: string) => {
    if (!size) return '';
    const bytes = parseInt(size);
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (!open) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.5)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: 'var(--bg-card)', borderRadius: 12, width: '90%', maxWidth: 640, maxHeight: '80vh',
        display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '16px 20px', borderBottom: '1px solid #e0ddd8',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {folderStack.length > 1 && (
              <button onClick={navigateBack} style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: 4,
                display: 'flex', alignItems: 'center',
              }}>
                <ArrowLeft size={18} />
              </button>
            )}
            <h3 style={{ margin: 0, fontSize: '1rem' }}>
              Google Drive — {currentFolder.name}
            </h3>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 4,
          }}>
            <X size={20} />
          </button>
        </div>

        {/* Breadcrumb */}
        {folderStack.length > 1 && (
          <div style={{ padding: '8px 20px', fontSize: '0.8rem', color: 'var(--text-muted)', background: 'var(--bg)' }}>
            {folderStack.map((f, i) => (
              <span key={i}>
                {i > 0 && ' / '}
                <span
                  style={{ cursor: i < folderStack.length - 1 ? 'pointer' : 'default', textDecoration: i < folderStack.length - 1 ? 'underline' : 'none' }}
                  onClick={() => {
                    if (i < folderStack.length - 1) {
                      setFolderStack(folderStack.slice(0, i + 1));
                    }
                  }}
                >
                  {f.name}
                </span>
              </span>
            ))}
          </div>
        )}

        {/* File List */}
        <div style={{ flex: 1, overflow: 'auto', padding: '8px 0' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
              <Loader size={24} className="spin" /> Dateien werden geladen...
            </div>
          ) : files.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
              Keine Dateien in diesem Ordner
            </div>
          ) : (
            files.map((file) => (
              <div key={file.id} style={{
                display: 'flex', alignItems: 'center', padding: '10px 20px', gap: 12,
                borderBottom: '1px solid #f0ede8',
                cursor: isFolder(file) ? 'pointer' : 'default',
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f8f5f0'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              onClick={() => isFolder(file) && navigateToFolder(file)}
              >
                {isFolder(file) ? (
                  <Folder size={20} style={{ color: '#F4B400', flexShrink: 0 }} />
                ) : (
                  <FileText size={20} style={{ color: '#4285F4', flexShrink: 0 }} />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.9rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {file.name}
                  </div>
                  {!isFolder(file) && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      {formatSize(file.size)}
                      {file.modifiedTime && ` — ${new Date(file.modifiedTime).toLocaleDateString('de-DE')}`}
                    </div>
                  )}
                </div>
                {!isFolder(file) && (
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button
                      className="btn btn-sm btn-secondary"
                      disabled={actionLoading === file.id}
                      onClick={(e) => { e.stopPropagation(); handleLink(file); }}
                      title="Verknüpfen (bleibt in Drive)"
                    >
                      <Link size={13} /> Verknüpfen
                    </button>
                    <button
                      className="btn btn-sm btn-primary"
                      disabled={actionLoading === file.id}
                      onClick={(e) => { e.stopPropagation(); handleImport(file); }}
                      title="Lokal importieren (herunterladen)"
                    >
                      <Download size={13} /> Importieren
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
